/**
 * Shared hook for table header logic
 * Consolidates logic for TableHeader component
 */

import { useState, useEffect, useMemo, useRef } from 'react';

export interface FilterItem {
  id: string;
  label: any;
  count?: number;
  hasSpotShipment?: boolean;
}

export interface UseTableHeaderLogicOptions {
  filterItems: FilterItem[];
  initialSelectedValues?: string[];
  useAutoSelectAll?: boolean; // If true, auto-select all on first load (TableHeader behavior)
  onOpen?: () => void;
  initialSpotFilter?: boolean; // If true, restore "Only Show SPOT" toggle state
}

export const useTableHeaderLogic = ({
  filterItems,
  initialSelectedValues = [],
  useAutoSelectAll = false,
  onOpen,
  initialSpotFilter = false,
}: UseTableHeaderLogicOptions) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyShowSpot, setOnlyShowSpot] = useState(initialSpotFilter);
  const hasInitializedRef = useRef(false);

  // Initialize selected values
  const [selectedValues, setSelectedValues] = useState<string[]>(() => {
    if (useAutoSelectAll && initialSelectedValues.length === 0) {
      return [];
    }
    return initialSelectedValues || [];
  });

  // Sync with parent when initialSelectedValues changes (AuditTableHeader behavior)
  useEffect(() => {
    if (!useAutoSelectAll && initialSelectedValues) {
      setSelectedValues(initialSelectedValues);
    }
  }, [initialSelectedValues, useAutoSelectAll]);

  // Auto-select all on first load (TableHeader behavior)
  useEffect(() => {
    if (useAutoSelectAll && !hasInitializedRef.current && filterItems?.length > 0) {
      if (initialSelectedValues.length > 0) {
        // Use initial values from URL params
        setSelectedValues(initialSelectedValues);
      } else {
        // Select all by default only on first load
        const allItemIds = filterItems.map((item) => item.id);
        setSelectedValues(allItemIds);
      }
      hasInitializedRef.current = true;
    }
  }, [initialSelectedValues, filterItems, useAutoSelectAll]);

  // Track previous filterItems length to detect when new items are loaded
  const prevFilterItemsLengthRef = useRef(filterItems.length);
  const isActiveSessionRef = useRef(false);

  // Filter items based on search term and spot filter
  const filteredItems = useMemo(
    () =>
      filterItems.filter((item) => {
        // Apply spot filter first
        if (onlyShowSpot && !item.hasSpotShipment) {
          return false;
        }

        // Handle null, undefined, empty or 'null' string labels
        if (!item.label || item.label === 'null' || item.label === 'undefined') {
          return searchTerm === '' || '-'.includes(searchTerm.toLowerCase());
        }
        // Normal string search for non-empty labels
        return String(item.label).toLowerCase().includes(searchTerm.toLowerCase());
      }),
    [filterItems, searchTerm, onlyShowSpot]
  );

  const open = Boolean(anchorEl);

  // Mark as active session when filter opens
  useEffect(() => {
    if (open) {
      isActiveSessionRef.current = true;
    }
  }, [open]);

  // Auto-select newly loaded items when filterItems length increases
  useEffect(() => {
    // Only run after initial load is complete
    if (!useAutoSelectAll || !hasInitializedRef.current) {
      return;
    }

    const currentLength = filterItems.length;
    const prevLength = prevFilterItemsLengthRef.current;

    // Check if new items were added (length increased)
    if (currentLength > prevLength) {
      // Only auto-select if this is an active session (user is scrolling/loading more)
      // AND we're not just reopening the filter (prevLength should be > 0)
      if (isActiveSessionRef.current && prevLength > 0) {
        // Get IDs of items that existed before this update
        const previousItemIds = filterItems.slice(0, prevLength).map((item) => item.id);

        // Check if all previous items are currently selected
        const allPreviousSelected = previousItemIds.every((id) => selectedValues.includes(id));

        if (allPreviousSelected) {
          // If all previous items are selected, auto-select the new items too
          const newItemIds = filterItems.slice(prevLength).map((item) => item.id);
          setSelectedValues((prev) => [...prev, ...newItemIds]);
        }
      }
    }

    // Update the ref for next comparison
    prevFilterItemsLengthRef.current = currentLength;
  }, [filterItems.length, useAutoSelectAll, filterItems, selectedValues, open]);

  useEffect(() => {
    if (open && onOpen) {
      onOpen();
    }
  }, [open, onOpen]);

  const handleHeaderClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
    // Reset active session flag and previous length when closing
    // This ensures clean state when filter reopens
    isActiveSessionRef.current = false;
    prevFilterItemsLengthRef.current = 0;
    // Don't reset onlyShowSpot here - it will be controlled by parent via initialSpotFilter
  };

  const handleSelectAll = () => {
    const valuesToSelect = filteredItems.map((item) => item.id);
    setSelectedValues(valuesToSelect);
  };

  const handleClearAll = () => {
    setSelectedValues([]);
  };

  const handleToggleValue = (itemId: any) => {
    setSelectedValues((prev) => {
      const newValues = prev.includes(itemId) ? prev.filter((v) => v !== itemId) : [...prev, itemId];
      return newValues;
    });
  };

  // Calculate counter for inline display
  const getInlineCounter = () => {
    // Use all filterItems (not filteredItems) for counter calculation
    // This ensures counter shows total selected, not just visible filtered items
    const availableItemIds = filterItems.map((item) => item.id);
    const selectedAvailableCount = selectedValues.filter((value) => availableItemIds.includes(value)).length;

    // Show counter if:
    // 1. Some items are selected (> 0)
    // 2. AND either:
    //    a) Not all items are selected (< total), OR
    //    b) SPOT filter is active (onlyShowSpot is true) - always show counter for filtered view
    const shouldShowCounter = selectedAvailableCount > 0 && (selectedAvailableCount < availableItemIds.length || onlyShowSpot);
    return { shouldShowCounter, selectedAvailableCount };
  };

  return {
    // State
    anchorEl,
    open,
    searchTerm,
    selectedValues,
    filteredItems,
    onlyShowSpot,

    // Handlers
    setSearchTerm,
    setSelectedValues,
    handleHeaderClick,
    handleClose,
    handleSelectAll,
    handleClearAll,
    handleToggleValue,
    setOnlyShowSpot,

    // Helper
    getInlineCounter,
  };
};

/**
 * Helper function to format labels consistently for display
 */
export const formatLabel = (label: any): string => {
  if (label === null || label === undefined || label === '' || label === 'null' || label === 'undefined') {
    return '-';
  }
  return String(label);
};
